import React, { useState } from 'react';
import { View, Text } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramFittingLossEquivalentLength } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { paToInWg, inWgToPa } from '../../../lib/calcs/conversions';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.airflowFittingLoss.v2';

type Fields = {
  // ΔP = K·ρV²/2 group
  kLoss: string;         // K (unitless)
  density: string;       // ρ (kg/m³)
  velocity: string;      // V in display velocity unit
  deltaP: string;        // ΔP in display pressure unit
  // L_eq = K·D_h / f group
  kEq: string;           // K for eq length
  dh: string;            // D_h in display diameter unit
  friction: string;      // f (unitless)
  lEq: string;           // L_eq in display length unit
};

const EMPTY: Fields = {
  kLoss: '',
  density: '1.2',
  velocity: '',
  deltaP: '',
  kEq: '',
  dh: '',
  friction: '',
  lEq: '',
};

/**
 * Fitting Loss & Equivalent Length — two bidirectional groups.
 *   ΔP = K · ρ · V² / 2        (4-field: K, ρ, V, ΔP)
 *   L_eq = K · D_h / f          (4-field: K, D_h, f, L_eq)
 */
const AirflowFittingLossEquivalentLengthScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  // Velocity: internal SI (m/s). Imperial base is fpm -> m/s via 0.00508.
  const vToMs = (s: string) => {
    const n = toImp(s, 'velocity'); // fpm
    return Number.isFinite(n) ? n * 0.00508 : NaN;
  };
  const vFromMs = (ms: number, dec = 2) => {
    if (!Number.isFinite(ms)) return '';
    return fromImp(ms / 0.00508, 'velocity', dec);
  };

  // ΔP internal Pa
  const dpToPa = (s: string) => {
    const n = toImp(s, 'pressure'); // inWg
    return Number.isFinite(n) ? inWgToPa(n) : NaN;
  };
  const dpFromPa = (pa: number, dec = 4) => {
    if (!Number.isFinite(pa)) return '';
    return fromImp(paToInWg(pa), 'pressure', dec);
  };

  // D_h internal meters (from inches)
  const dhToM = (s: string) => {
    const n = toImp(s, 'diameter'); // inches
    return Number.isFinite(n) ? n * 0.0254 : NaN;
  };
  const dhFromM = (m: number, dec = 2) => {
    if (!Number.isFinite(m)) return '';
    return fromImp(m / 0.0254, 'diameter', dec);
  };

  // L_eq internal meters (from feet)
  const lToM = (s: string) => {
    const n = toImp(s, 'length'); // feet
    return Number.isFinite(n) ? n * 0.3048 : NaN;
  };
  const lFromM = (m: number, dec = 1) => {
    if (!Number.isFinite(m)) return '';
    return fromImp(m / 0.3048, 'length', dec);
  };

  const calculate = () => {
    setError(null);

    const anyGroupFilled =
      [fields.kLoss, fields.density, fields.velocity, fields.deltaP].some((v) => v !== '') ||
      [fields.kEq, fields.dh, fields.friction, fields.lEq].some((v) => v !== '');

    if (!anyGroupFilled) {
      setError('Fill any 3 of 4 in either section to solve the missing one.');
      return;
    }

    // Group 1: ΔP = K·ρV²/2
    {
      const core = [fields.kLoss, fields.density, fields.velocity, fields.deltaP];
      const filled = core.filter((v) => v !== '').length;
      if (filled === 4) { setError('ΔP section: all 4 values filled — clear one to solve for it.'); return; }
      if (filled === 3) {
        const K = parseFloat(fields.kLoss);
        const rho = parseFloat(fields.density);
        const V = vToMs(fields.velocity);
        const dp = dpToPa(fields.deltaP);

        if (fields.deltaP === '') {
          if (!(K >= 0) || !(rho > 0) || !(V >= 0)) { setError('K ≥ 0, ρ > 0, V ≥ 0 required.'); return; }
          setSolvedFor('deltaP');
          set({ deltaP: dpFromPa(K * rho * V * V / 2) });
          return;
        } else if (fields.velocity === '') {
          if (!(K > 0) || !(rho > 0) || !(dp >= 0)) { setError('K > 0, ρ > 0, ΔP ≥ 0 required.'); return; }
          setSolvedFor('velocity');
          set({ velocity: vFromMs(Math.sqrt(2 * dp / (K * rho))) });
          return;
        } else if (fields.density === '') {
          if (!(K > 0) || !(V > 0) || !(dp >= 0)) { setError('K > 0, V > 0, ΔP ≥ 0 required.'); return; }
          setSolvedFor('density');
          set({ density: parseFloat((2 * dp / (K * V * V)).toFixed(4)).toString() });
          return;
        } else if (fields.kLoss === '') {
          if (!(rho > 0) || !(V > 0) || !(dp >= 0)) { setError('ρ > 0, V > 0, ΔP ≥ 0 required.'); return; }
          setSolvedFor('kLoss');
          set({ kLoss: parseFloat((2 * dp / (rho * V * V)).toFixed(4)).toString() });
          return;
        }
      }
    }

    // Group 2: L_eq = K · D_h / f
    {
      const core = [fields.kEq, fields.dh, fields.friction, fields.lEq];
      const filled = core.filter((v) => v !== '').length;
      if (filled === 4) { setError('L_eq section: all 4 values filled — clear one to solve for it.'); return; }
      if (filled === 3) {
        const K = parseFloat(fields.kEq);
        const Dh = dhToM(fields.dh);
        const f = parseFloat(fields.friction);
        const Le = lToM(fields.lEq);

        if (fields.lEq === '') {
          if (!(K >= 0) || !(Dh > 0) || !(f > 0)) { setError('K ≥ 0, D_h > 0, f > 0 required.'); return; }
          setSolvedFor('lEq');
          set({ lEq: lFromM(K * Dh / f) });
          return;
        } else if (fields.friction === '') {
          if (!(K > 0) || !(Dh > 0) || !(Le > 0)) { setError('K > 0, D_h > 0, L_eq > 0 required.'); return; }
          setSolvedFor('friction');
          set({ friction: parseFloat((K * Dh / Le).toFixed(4)).toString() });
          return;
        } else if (fields.dh === '') {
          if (!(K > 0) || !(f > 0) || !(Le >= 0)) { setError('K > 0, f > 0, L_eq ≥ 0 required.'); return; }
          setSolvedFor('dh');
          set({ dh: dhFromM(Le * f / K) });
          return;
        } else if (fields.kEq === '') {
          if (!(Dh > 0) || !(f > 0) || !(Le >= 0)) { setError('D_h > 0, f > 0, L_eq ≥ 0 required.'); return; }
          setSolvedFor('kEq');
          set({ kEq: parseFloat((Le * f / Dh).toFixed(4)).toString() });
          return;
        }
      }
    }

    // If we reach here, no group had exactly 3 filled.
    setError('Fill any 3 of 4 in either section to solve the missing field.');
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.deltaP && { label: 'ΔP (fittings)', value: fields.deltaP, unit: unit('pressure'), emphasis: solvedFor === 'deltaP' },
    fields.velocity && { label: 'V', value: fields.velocity, unit: unit('velocity'), emphasis: solvedFor === 'velocity' },
    fields.lEq && { label: 'Equivalent Length', value: fields.lEq, unit: unit('length'), emphasis: solvedFor === 'lEq' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Fitting Loss & Equivalent Length"
      subtitle="Two bidirectional groups — fill any 3 of 4 in each section."
      formula={
        <>
          <FormulaLine>ΔP = K · ρ · V² / 2</FormulaLine>
          <FormulaLine>L_eq = K · D_h / f</FormulaLine>
          <FormulaNote>Reference: ASHRAE Fundamentals (loss coefficient method). ρ in kg/m³.</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formulas: ΔP = K·ρV²/2 ; L_eq = K·Dh/f"
      diagram={<DiagramFittingLossEquivalentLength />}
      outputSlots={{
        velocity_fpm: fields.velocity ? toImp(fields.velocity, 'velocity') : undefined,
        static_pressure_iwg: fields.deltaP ? toImp(fields.deltaP, 'pressure') : undefined,
        duct_diameter_in: fields.dh ? toImp(fields.dh, 'diameter') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.velocity_fpm != null && !fields.velocity) {
          patch.velocity = fromImp(values.velocity_fpm, 'velocity', 2);
        }
        if (values.static_pressure_iwg != null && !fields.deltaP) {
          patch.deltaP = fromImp(values.static_pressure_iwg, 'pressure', 4);
        }
        if (values.duct_diameter_in != null && !fields.dh) {
          patch.dh = fromImp(values.duct_diameter_in, 'diameter', 2);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <Text style={{ fontWeight: '600', marginBottom: 8 }}>Fitting ΔP — fill any 3 of 4</Text>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="K (loss coefficient)" value={fields.kLoss} onChangeText={(v) => set({ kLoss: v })} maxDecimals={4} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Density ρ (kg/m³)" value={fields.density} onChangeText={(v) => set({ density: v })} rightText="kg/m³" maxDecimals={4} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Velocity V (${unit('velocity')})`} value={fields.velocity} onChangeText={(v) => set({ velocity: v })} rightText={unit('velocity')} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 16 }}>
        <NumericField label={`ΔP (${unit('pressure')})`} value={fields.deltaP} onChangeText={(v) => set({ deltaP: v })} rightText={unit('pressure')} maxDecimals={4} onSubmitEditing={calculate} />
      </View>

      <Text style={{ fontWeight: '600', marginBottom: 8, marginTop: 4 }}>Equivalent Length — fill any 3 of 4</Text>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="K (loss coefficient)" value={fields.kEq} onChangeText={(v) => set({ kEq: v })} maxDecimals={4} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Hydraulic Diameter D_h (${unit('diameter')})`} value={fields.dh} onChangeText={(v) => set({ dh: v })} rightText={unit('diameter')} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Friction factor f" value={fields.friction} onChangeText={(v) => set({ friction: v })} maxDecimals={4} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`L_eq (${unit('length')})`} value={fields.lEq} onChangeText={(v) => set({ lEq: v })} rightText={unit('length')} maxDecimals={1} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default AirflowFittingLossEquivalentLengthScreen;
